import React, { useCallback, useEffect, useRef, useState } from 'react';
import BlogIcon from './BlogIcon';

interface ShareControlsProps {
  url: string;
  title: string;
  /** چیدمان موبایل: دو دکمهٔ تمام‌عرض ۴۴px */
  variant: 'desktop' | 'mobile';
}

const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback below */
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
};

/** دکمه‌های «اشتراک‌گذاری» و «کپی لینک» + منوی اشتراک + توست */
const ShareControls: React.FC<ShareControlsProps> = ({ url, title, variant }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleCopy = async () => {
    const ok = await copyText(url);
    showToast(ok ? 'لینک مقاله با موفقیت کپی شد' : 'کپی لینک ممکن نشد؛ آدرس را از نوار مرورگر کپی کنید');
    setOpen(false);
  };

  const handleShare = async () => {
    // موبایل: اگر Web Share هست همان را باز می‌کنیم
    if (variant === 'mobile' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        /* لغو کاربر یا عدم پشتیبانی → منوی خودمان */
      }
    }
    setOpen((v) => !v);
  };

  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const telegramHref = `https://t.me/share/url?url=${encoded}&text=${encodedTitle}`;
  const xHref = `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`;

  const buttonClass =
    variant === 'mobile'
      ? 'flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-[8px] border border-[#e9e8e5] bg-[#f4f3f1] px-[14px] py-[10px] text-[14px] font-medium leading-6 text-[#1a1c1a] transition-colors hover:bg-[#efeeeb]'
      : 'inline-flex items-center gap-[6px] rounded-[8px] border border-[#e9e8e5] bg-white px-3 py-[6px] text-[12px] font-medium leading-5 text-[#5b5f66] transition-colors hover:bg-[#f4f3f1]';

  return (
    <div ref={wrapRef} className={`relative flex items-center ${variant === 'mobile' ? 'w-full gap-[10px]' : 'gap-2'}`}>
      {/* طبق طرح: دسکتاپ «کپی لینک» سمت راست و «اشتراک‌گذاری» چپ؛ موبایل برعکس. آیکون سمت راست متن */}
      <button type="button" onClick={handleShare} aria-haspopup="menu" aria-expanded={open} className={`${buttonClass} ${variant === 'desktop' ? 'order-2' : 'order-1'}`}>
        <BlogIcon name={variant === 'mobile' ? 'share-mobile' : 'share'} width={variant === 'mobile' ? 13.5 : 12} height={variant === 'mobile' ? 15 : 13.333} />
        اشتراک‌گذاری
      </button>
      <button type="button" onClick={handleCopy} className={`${buttonClass} ${variant === 'desktop' ? 'order-1' : 'order-2'}`}>
        <BlogIcon name={variant === 'mobile' ? 'copy-mobile' : 'copy-link'} width={variant === 'mobile' ? 12.75 : 13.333} height={variant === 'mobile' ? 15 : 6.667} />
        کپی لینک
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 flex w-56 flex-col gap-1 rounded-[8px] border border-[#e6e4df] bg-[#f4f3f1] px-[10px] pb-[10px] pt-[14px] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]"
        >
          <span className="px-2 py-1 text-[11px] font-bold leading-4 text-[#666]">اشتراک‌گذاری در:</span>
          <button type="button" role="menuitem" onClick={handleCopy} className="flex items-center justify-between rounded-[4px] px-2 py-[6px] text-[12px] leading-4 text-[#222] hover:bg-white">
            کپی لینک مستقیم
            <BlogIcon name="copy-sm" width={14} height={14} />
          </button>
          <a role="menuitem" href={telegramHref} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-[4px] px-2 py-[6px] text-[12px] leading-4 text-[#222] hover:bg-white">
            پیام‌رسان تلگرام
            <span className="text-[10px] text-[#666]">Telegram</span>
          </a>
          <a role="menuitem" href={xHref} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-[4px] px-2 py-[6px] text-[12px] leading-4 text-[#222] hover:bg-white">
            ایکس (توییتر)
            <span className="text-[10px] text-[#666]">X</span>
          </a>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="blog-toast fixed bottom-6 left-1/2 z-50 flex w-[min(336px,calc(100vw-32px))] -translate-x-1/2 items-center justify-between rounded-[8px] bg-[#222] px-[14px] py-[10px] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]"
        >
          <span className="inline-flex items-center gap-2 text-[12px] leading-4 text-white">
            <BlogIcon name="toast-check" width={16} height={16} />
            {toast}
          </span>
          <span className="text-[10px] leading-4 text-[#d1d5db]">آماده اشتراک</span>
        </div>
      )}
    </div>
  );
};

export default ShareControls;
